/**
 * Sysadmin Catalog → Skills hooks (PR-I).
 *
 * Wraps `/api/v1/admin/platform/catalog/skills/*` and the
 * `/admin/platform/tenant-skill-assignments` endpoints. Mirrors the
 * pattern in `use-sysadmin-prompts.ts` and `use-sysadmin-system-catalog.ts`,
 * with publish-lifecycle helpers added.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Types ────────────────────────────────────────────────────────────────

export type SkillStatus = 'draft' | 'published' | 'deprecated' | 'retired';

export interface SkillVariable {
  name: string;
  label?: string;
  type: string;
  description?: string;
  required?: boolean;
  default?: string;
  options?: string[];
}

export interface CatalogSkill {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies: string[];
  functionDependencies: string[];
  variables: SkillVariable[];
  examples: unknown[];
  category: string;
  version: number;
  isActive: boolean;
  status: SkillStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  assignmentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogSkillCompositionRow {
  id: string;
  slug?: string;
  functionKey?: string;
  displayName: string;
  isActive: boolean;
}

export interface CatalogSkillVersionEntry {
  id: string;
  version: number;
  changedAt: string;
  changedByAdmin: string | null;
}

export interface CatalogSkillDetail extends CatalogSkill {
  composition: {
    tools: CatalogSkillCompositionRow[];
    functions: CatalogSkillCompositionRow[];
    danglingToolIds: string[];
    danglingFunctionIds: string[];
  };
  versions: CatalogSkillVersionEntry[];
}

export interface CatalogSkillAssignment {
  id: string;
  systemSkillId: string;
  organizationId: string;
  organizationName: string | null;
  assignedBy: string | null;
  assignedAt: string;
  notes: string | null;
}

export interface CatalogSkillListResponse {
  items: CatalogSkill[];
  nextCursor: string | null;
}

const BASE = '/admin/platform';

const KEYS = {
  list: (filters: Record<string, unknown>) =>
    ['sysadmin-catalog-skills', 'list', filters] as const,
  detail: (id: string) =>
    ['sysadmin-catalog-skills', 'detail', id] as const,
  assignments: (id: string) =>
    ['sysadmin-catalog-skills', 'assignments', id] as const,
};

// ─── List + detail ────────────────────────────────────────────────────────

export interface UseCatalogSkillsParams {
  status?: SkillStatus;
  category?: string;
  search?: string;
  limit?: number;
}

/**
 * Cursor-paginated infinite query for the list view. The page table
 * concatenates `data.pages.flatMap(p => p.items)`.
 */
export function useCatalogSkills(params: UseCatalogSkillsParams = {}) {
  return useInfiniteQuery<CatalogSkillListResponse, Error>({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.category) qs.set('category', params.category);
      if (params.search) qs.set('search', params.search);
      if (params.limit) qs.set('limit', String(params.limit));
      if (pageParam) qs.set('cursor', String(pageParam));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiClient.get<CatalogSkillListResponse>(
        `${BASE}/catalog/skills${query}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useCatalogSkill(id: string | null | undefined) {
  return useQuery<{ data: CatalogSkillDetail }, Error>({
    queryKey: id ? KEYS.detail(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ data: CatalogSkillDetail }>(
        `${BASE}/catalog/skills/${id}`,
      ),
    enabled: !!id,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export interface CreateCatalogSkillInput {
  slug: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies?: string[];
  functionDependencies?: string[];
  variables?: SkillVariable[];
  examples?: Record<string, unknown>[];
  category?: string;
}

export function useCreateCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogSkill }, Error, CreateCatalogSkillInput>({
    mutationFn: (body) =>
      apiClient.post<{ data: CatalogSkill }>(
        `${BASE}/catalog/skills`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills'] });
    },
  });
}

export interface UpdateCatalogSkillInput
  extends Partial<Omit<CreateCatalogSkillInput, 'slug'>> {
  id: string;
}

export function useUpdateCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogSkill }, Error, UpdateCatalogSkillInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: CatalogSkill }>(
        `${BASE}/catalog/skills/${id}`,
        body,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: true }>(`${BASE}/catalog/skills/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills'] });
    },
  });
}

export function usePublishCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogSkill }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: CatalogSkill }>(
        `${BASE}/catalog/skills/${id}/publish`,
        {},
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export interface UnpublishCatalogSkillInput {
  id: string;
  reason: string;
  nextStatus?: 'draft' | 'deprecated' | 'retired';
}

export function useUnpublishCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogSkill },
    Error,
    UnpublishCatalogSkillInput
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<{ data: CatalogSkill }>(
        `${BASE}/catalog/skills/${id}/unpublish`,
        { reason, nextStatus: nextStatus ?? 'deprecated' },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

// ─── Assignments ──────────────────────────────────────────────────────────

export function useCatalogSkillAssignments(id: string | null | undefined) {
  return useQuery<{ items: CatalogSkillAssignment[] }, Error>({
    queryKey: id ? KEYS.assignments(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ items: CatalogSkillAssignment[] }>(
        `${BASE}/catalog/skills/${id}/assignments`,
      ),
    enabled: !!id,
  });
}

export interface AssignSkillInput {
  systemSkillId: string;
  organizationId: string;
  notes?: string;
}

export function useAssignCatalogSkill() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogSkillAssignment; alreadyExisted: boolean },
    Error,
    AssignSkillInput
  >({
    mutationFn: (body) =>
      apiClient.post<{
        data: CatalogSkillAssignment;
        alreadyExisted: boolean;
      }>(`${BASE}/tenant-skill-assignments`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.assignments(vars.systemSkillId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.systemSkillId) });
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills', 'list'] });
    },
  });
}

export function useRevokeCatalogSkillAssignment() {
  const qc = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { id: string; systemSkillId: string }
  >({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: true }>(
        `${BASE}/tenant-skill-assignments/${id}`,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.assignments(vars.systemSkillId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.systemSkillId) });
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-skills', 'list'] });
    },
  });
}
